export const metricKeys = ['level', 'exp', 'hp', 'mp', 'mesos', 'mapName'] as const

export type MetricKey = (typeof metricKeys)[number]
export type MetricStrategy = 'bar' | 'ocr' | 'fused' | 'unavailable'
export type ReadingSource = 'bar' | 'ocr' | 'fused'
export type Availability = 'available' | 'unavailable'

export interface NormalizedRect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export interface PixelRect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export interface MetricReading<T = number> {
  readonly value: T | null
  readonly confidence: number
  readonly source: ReadingSource
  readonly availability: Availability
  readonly rawText?: string
  readonly ocrValue?: T
  readonly resolvedBy?: 'exp-table'
  /** The recognizer has already confirmed this value across independent OCR samples. */
  readonly confirmation?: 'consensus'
  readonly capturedAt: number
}

export interface ResourceValue {
  readonly current: number | null
  readonly max: number | null
  readonly ratio: number | null
}

export interface ExpValue {
  readonly absolute: number | null
  readonly ratio: number | null
  readonly estimated?: boolean
}

export type MetricDiagnosticValue = number | string | ExpValue | ResourceValue
export type MetricRecognitionStatus = 'ready' | 'confirming' | 'source-hidden' | 'no-result'

export interface MetricRecognitionDiagnostic {
  readonly status: MetricRecognitionStatus
  readonly value?: MetricDiagnosticValue
  readonly rawText?: string
  readonly confidence?: number
  readonly enhancementIndex?: number
  readonly resolvedBy?: 'exp-table'
  readonly capturedAt: number
}

export interface RawReading {
  readonly capturedAt: number
  readonly level: MetricReading
  readonly exp: MetricReading<ExpValue>
  readonly hp: MetricReading<ResourceValue>
  readonly mp: MetricReading<ResourceValue>
  readonly mesos: MetricReading
  readonly mapName: MetricReading<string>
  readonly diagnostics: {
    readonly hudConfidence: number
    readonly processingMs: number
    readonly rejectedReason?: string
    readonly metrics?: Readonly<Partial<Record<MetricKey, MetricRecognitionDiagnostic>>>
  }
}

export interface ValidatedObservation extends RawReading {
  readonly accepted: Readonly<Record<MetricKey, boolean>>
  /** A confirmed same-level EXP decrease that must start a new accounting baseline. */
  readonly expRebaseline?: true
}

export interface GameStateSnapshot {
  readonly capturedAt: number
  readonly observedAt?: Readonly<Record<MetricKey, number | null>>
  readonly level: number | null
  readonly exp: ExpValue
  readonly hp: ResourceValue
  readonly mp: ResourceValue
  readonly mesos: number | null
  readonly mapName: string | null
  readonly confidence: Readonly<Record<MetricKey, number>>
  readonly coverage: Readonly<Record<MetricKey, number>>
}

export type GameEventType =
  | 'level-up'
  | 'hp-consumed'
  | 'hp-recovered'
  | 'mp-consumed'
  | 'mp-recovered'
  | 'mesos-changed'
  | 'map-changed'
  | 'capture-interrupted'
  | 'capture-restored'

export interface GameEvent {
  readonly id: string
  readonly sessionId: string
  readonly type: GameEventType
  readonly capturedAt: number
  readonly amount?: number
  readonly from?: number
  readonly to?: number
  readonly previousMapName?: string
  readonly mapName?: string
  readonly confidence: number
}

export type SessionStatus = 'idle' | 'running' | 'paused' | 'ended' | 'interrupted'

export interface SessionAggregate {
  readonly durationMs: number
  readonly expGained: number | null
  /** Trusted contiguous EXP used as the hourly-rate numerator. */
  readonly expRateGained?: number | null
  /** Trusted contiguous EXP observation time used by the hourly rate. */
  readonly expTrustedDurationMs?: number
  readonly expPerHour: number | null
  /** Optional for records saved before session balance tracking was introduced. */
  readonly mesosStartBalance?: number | null
  /** Optional for records saved before session balance tracking was introduced. */
  readonly mesosCurrentBalance?: number | null
  /** Current confirmed balance minus the first confirmed balance in this record. */
  readonly mesosBalanceChange?: number | null
  readonly mesosDelta: number | null
  /** Trusted visible inventory time used by the Mesos hourly rate. */
  readonly mesosTrustedDurationMs?: number
  readonly mesosPerHour: number | null
  readonly hpConsumed: number
  readonly hpConsumedPerHour: number | null
  readonly hpRecovered: number
  readonly hpRecoveredPerHour: number | null
  readonly mpConsumed: number
  readonly mpConsumedPerHour: number | null
  readonly mpRecovered: number
  readonly mpRecoveredPerHour: number | null
  readonly coverage: Readonly<Record<MetricKey, number>>
}

export type LiveRateStatus = 'ready' | 'unavailable'

export interface LiveRateMetric {
  readonly value: number | null
  readonly status: LiveRateStatus
  readonly trustedDurationMs: number
  readonly updatedAt: number | null
}

export interface LiveRateSnapshot {
  readonly expPerHour: LiveRateMetric
  readonly mesosPerHour: LiveRateMetric
  readonly hpConsumedPerHour: LiveRateMetric
  readonly hpRecoveredPerHour: LiveRateMetric
  readonly mpConsumedPerHour: LiveRateMetric
  readonly mpRecoveredPerHour: LiveRateMetric
}

export interface SessionRecord {
  readonly id: string
  readonly presetId: string
  readonly presetVersion: number
  readonly profileId: string
  readonly status: SessionStatus
  readonly startedAt: number
  readonly endedAt?: number
  readonly activeDurationMs: number
  readonly pausedAt?: number
  readonly aggregate: SessionAggregate
  readonly startState?: GameStateSnapshot
  readonly endState?: GameStateSnapshot
}

export type AccountingContinuity = 'ignored' | 'baseline' | 'rebaseline' | 'contiguous' | 'discontinuous' | 'unavailable' | 'pending'

export interface AccountingMetricUpdate {
  readonly continuity: AccountingContinuity
  readonly sourceAt: number | null
  readonly trustedIntervalMs: number
}

export interface AccountingExpUpdate extends AccountingMetricUpdate {
  readonly delta: number | null
  readonly exact: boolean
  readonly start: RankExpPoint | null
  readonly end: RankExpPoint | null
}

export interface AccountingMesosUpdate extends AccountingMetricUpdate {
  readonly delta: number | null
  readonly startValue: number | null
  readonly endValue: number | null
}

export interface AccountingResourceUpdate extends AccountingMetricUpdate {
  readonly consumed: number
  readonly recovered: number
}

export interface AccountingFrame {
  readonly capturedAt: number
  readonly captureGeneration: number
  readonly exp: AccountingExpUpdate
  readonly mesos: AccountingMesosUpdate
  readonly hp: AccountingResourceUpdate
  readonly mp: AccountingResourceUpdate
}

export interface SessionIngestResult {
  readonly record: SessionRecord
  readonly accounting: AccountingFrame
}

export interface CaptureProfile {
  readonly id: string
  readonly name: string
  readonly presetId: string
  readonly presetVersion: number
  readonly sourceWidth: number
  readonly sourceHeight: number
  readonly hudRect: NormalizedRect
  readonly roiOverrides: Partial<Record<MetricKey, NormalizedRect>>
  readonly colorCalibration: Partial<Record<'hp' | 'mp' | 'exp', readonly [number, number, number]>>
  readonly recognition: {
    readonly captureFps: number
    readonly metricCaptureFps?: Readonly<Partial<Record<MetricKey, number>>>
    readonly metricCaptureFpsVersion?: number
    readonly minConfidence: number
    readonly ocrChangeThreshold: number
  }
  readonly createdAt: number
  readonly updatedAt: number
}

export interface AppSetting<T = unknown> {
  readonly key: string
  readonly value: T
  readonly updatedAt: number
}

export type RankMetric = 'exp' | 'mesos'

export interface RankExpPoint {
  readonly level: number
  readonly absolute: number
}

export interface RankExpMetric {
  readonly gain: number
  readonly trustedDurationMs: number
  readonly start: RankExpPoint
  readonly end: RankExpPoint
}

export interface RankMesosMetric {
  readonly gain: number
  readonly trustedDurationMs: number
  readonly startValue: number
  readonly endValue: number
}

export interface RankSubmissionDraft {
  readonly submissionId: string
  readonly presetId: string
  readonly presetVersion: number
  readonly mapName: string
  readonly levelBucket: number
  readonly startedAt: number
  readonly endedAt: number
  readonly exp: RankExpMetric
  readonly mesos?: RankMesosMetric
}

export interface RankSubmission extends RankSubmissionDraft {
  readonly visitorId: string
}

export interface RankQueueItem {
  readonly id: string
  readonly accountingVersion: number
  readonly submission: RankSubmissionDraft
  readonly attempts: number
  readonly createdAt: number
  readonly updatedAt: number
  readonly nextAttemptAt: number
  readonly lastError?: string
}

export interface RankMetricSummary {
  readonly p25: number
  readonly p50: number
  readonly p75: number
  readonly p90: number
}

export interface RankMapItem {
  readonly rank: number | null
  readonly mapName: string
  readonly mapKey: string
  readonly levelBucket: number
  readonly metric: RankMetric
  readonly userCount: number
  readonly totalTrustedDurationMs: number
  readonly percentiles: RankMetricSummary
  readonly qualified: boolean
  readonly updatedAt: number
}

export interface RankMapPage {
  readonly formal: {
    readonly items: readonly RankMapItem[]
    readonly page: number
    readonly pageSize: number
    readonly total: number
  }
  readonly accumulating: {
    readonly items: readonly RankMapItem[]
    readonly total: number
  }
  readonly generatedAt: number
}

export interface RankMeta {
  readonly presetId: string
  readonly levelBuckets: readonly number[]
  readonly minimumUsers: number
  readonly minimumTrustedDurationMs: number
  readonly lastUpdatedAt: number | null
}
